import { mergeAttributes } from '@tiptap/core';
import { Mention, MentionOptions } from '@tiptap/extension-mention';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    userMention: {
      setMentionDropdownActive: (value: boolean) => ReturnType;
    };
  }
}

export default Mention.extend<MentionOptions>({
  selectable: true,

  addOptions(): any {
    const parentOptions = this.parent?.() ?? ({} as MentionOptions);
    const { suggestion } = parentOptions;
    if (suggestion) {
      suggestion.command = ({ editor, range, props }: { editor: any; range: any; props: any }) => {
        // increase range.to by one when the next node is of type "text"
        // and starts with a space character
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(' ');

        if (overrideSpace) {
          range.to += 1;
        }
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: props.nodeName,
              attrs: props,
            },
            {
              type: 'text',
              text: ' ',
            },
          ])
          .run();

        window.getSelection()?.collapseToEnd();
      };
    }
    return {
      ...parentOptions,
      ...suggestion,
    };
  },

  addStorage() {
    return {
      mentionDropdownActive: false,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.user-mention',
      },
      {
        tag: 'user-mention',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'user-mention',
      mergeAttributes({ 'data-type': this.name }, this.options.HTMLAttributes, HTMLAttributes),
    ];
  },

  addAttributes() {
    return {
      userId: {
        default: null,
        parseHTML: (element) => {
          const userId =
            element.getAttribute('user-id') ?? element.firstElementChild?.getAttribute('user-id');
          return userId;
        },
        renderHTML: (attributes) => {
          if (!attributes.userId) {
            return {};
          }

          return {
            'user-id': attributes.userId,
          };
        },
      },
      mentionTime: {
        default: null,
        parseHTML: (element) => {
          const mentionTime =
            element.getAttribute('mention-time') ??
            element.firstElementChild?.getAttribute('mention-time');
          return mentionTime;
        },
        renderHTML: (attributes) => {
          if (!attributes.mentionTime) {
            return {};
          }

          return {
            'mention-time': attributes.mentionTime,
          };
        },
      },
      userValue: {
        default: null,
        parseHTML: (element) => {
          const userValue =
            element.getAttribute('user-value') ??
            element.firstElementChild?.getAttribute('user-value');
          return userValue;
        },
        renderHTML: (attributes) => {
          if (!attributes.userValue) {
            return {};
          }

          return {
            'user-value': attributes.userValue,
          };
        },
      },
      denotationChar: {
        default: null,
        parseHTML: (element) => {
          const denotationChar =
            element.getAttribute('denotation-char') ??
            element.firstElementChild?.getAttribute('denotation-char');
          return denotationChar;
        },
        renderHTML: (attributes) => {
          if (!attributes.denotationChar) {
            return { 'denotation-char': '@' };
          }

          return {
            'denotation-char': attributes.denotationChar,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      setMentionDropdownActive: (value: boolean) => () => {
        this.storage.mentionDropdownActive = value;
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    return [];
  },
});
